// Pasting from Word / Google Docs / web pages: keeps paragraphs, headings,
// bold, italic, underline, lists, quotes and links; drops fonts, colours,
// sizes, styles, classes, images and everything else. Word's fake lists
// (paragraphs with "mso-list") become real lists.
// Documents (cleanPaste(html, { tables: true })): tables from Word / Excel
// stay real tables — rows, columns, merged cells, bold, alignment (Excel's
// class-based styles included).

const HREF = /^(https?:\/\/|mailto:|\/(?!\/))/i;
const BLOCKS = { P: 'p', DIV: 'p', H1: 'h2', H2: 'h2', H3: 'h3', H4: 'h3', H5: 'h3', H6: 'h3', BLOCKQUOTE: 'blockquote', LI: 'li', UL: 'ul', OL: 'ol', TR: 'p' };
const SKIP = new Set(['SCRIPT', 'STYLE', 'META', 'LINK', 'TITLE', 'IMG', 'SVG', 'OBJECT', 'IFRAME', 'XML', 'O:P', 'HEAD', 'TEMPLATE']);

const css = (el, prop) => (el.getAttribute?.('style') || '').toLowerCase().replace(/\s/g, '').match(new RegExp(`(?:^|;)${prop}:([^;]+)`))?.[1] || '';
const isBold = (el) => /^(bold|bolder|[6-9]00)$/.test(css(el, 'font-weight'));
const isNotBold = (el) => /^(normal|[1-4]00)$/.test(css(el, 'font-weight'));

let opts = { tables: false };
let xlClasses = {}; // Excel: class → { b, align } from its <style>
const TABLE_TAGS = { TABLE: 'table', THEAD: 'thead', TBODY: 'tbody', TFOOT: 'tbody', TR: 'tr', TD: 'td', TH: 'th' };
function cellFrom(n, doc, tag) {
  const el = doc.createElement(tag);
  const cs = Number(n.getAttribute('colspan')); const rs = Number(n.getAttribute('rowspan'));
  if (cs > 1) el.setAttribute('colspan', String(Math.min(cs, 20)));
  if (rs > 1) el.setAttribute('rowspan', String(Math.min(rs, 20)));
  const xl = (n.getAttribute('class') || '').split(/\s+/).map((c) => xlClasses[c]).find(Boolean) || {};
  const align = (n.getAttribute('align') || css(n, 'text-align') || xl.align || '').toLowerCase();
  if (/^(center|right|justify)$/.test(align)) el.style.textAlign = align;
  const w = css(n, 'width');
  if (/^\d{1,3}(\.\d+)?%$/.test(w)) el.style.width = w;
  // Cell paragraphs → lines.
  const inner = walk(n, doc);
  const box = doc.createElement('div'); box.appendChild(inner);
  for (const p of [...box.querySelectorAll('p, h2, h3, li')]) { p.after(doc.createElement('br')); while (p.firstChild) p.before(p.firstChild); p.remove(); }
  for (const l of [...box.querySelectorAll('ul, ol, blockquote')]) { while (l.firstChild) l.before(l.firstChild); l.remove(); }
  while (box.lastChild?.nodeName === 'BR') box.lastChild.remove();
  if ((isBold(n) || xl.b) && tag === 'td' && box.textContent.trim()) { const b = doc.createElement('strong'); while (box.firstChild) b.appendChild(box.firstChild); box.appendChild(b); }
  while (box.firstChild) el.appendChild(box.firstChild);
  if (!el.textContent.trim()) el.innerHTML = '<br>';
  return el;
}
function walk(node, doc) {
  const frag = doc.createDocumentFragment();
  for (const n of [...node.childNodes]) {
    if (n.nodeType === 3) { frag.appendChild(doc.createTextNode(n.data.replace(/ /g, ' '))); continue; }
    if (n.nodeType !== 1) continue;
    const tag = n.tagName.toUpperCase();
    if (SKIP.has(tag) || /mso-list:\s*ignore/i.test(n.getAttribute('style') || '')) continue;
    if (tag === 'BR') { frag.appendChild(doc.createElement('br')); continue; }
    if (opts.tables && TABLE_TAGS[tag]) {
      if (tag === 'TD' || tag === 'TH') { frag.appendChild(cellFrom(n, doc, TABLE_TAGS[tag])); continue; }
      const el = doc.createElement(TABLE_TAGS[tag]);
      if (tag === 'TABLE') el.className = 'dt';
      // Only rows / cells inside (Word's and Excel's <col>, <colgroup> are dropped).
      for (const k of [...walk(n, doc).childNodes]) if (k.nodeType === 1 && /^(THEAD|TBODY|TR|TD|TH)$/.test(k.nodeName)) el.appendChild(k);
      frag.appendChild(el);
      continue;
    }
    const inner = walk(n, doc);
    let el = null;
    if (BLOCKS[tag]) el = doc.createElement(BLOCKS[tag]);
    else if (tag === 'A') {
      const href = (n.getAttribute('href') || '').trim();
      if (HREF.test(href)) { el = doc.createElement('a'); el.setAttribute('href', href); }
    } else if ((tag === 'B' || tag === 'STRONG') && !isNotBold(n)) el = doc.createElement('strong');
    else if (tag === 'I' || tag === 'EM') el = doc.createElement('em');
    else if (tag === 'U') el = doc.createElement('u');
    // Google Docs / Word put formatting in span styles.
    if (!el && (tag === 'SPAN' || tag === 'FONT')) {
      let wrap = inner;
      for (const [test, t] of [[isBold(n), 'strong'], [css(n, 'font-style') === 'italic', 'em'], [/underline/.test(css(n, 'text-decoration')), 'u']]) {
        if (!test) continue;
        const w = doc.createElement(t);
        w.appendChild(wrap);
        wrap = w;
      }
      frag.appendChild(wrap);
      continue;
    }
    if (el) { el.appendChild(inner); frag.appendChild(el); } else frag.appendChild(inner);
  }
  return frag;
}

// Word: consecutive "list paragraphs" → <ul>/<ol>.
function wordLists(root, doc) {
  const isItem = (el) => el?.tagName === 'P' && /mso-list/i.test(el.getAttribute('style') || '') || /MsoListParagraph/i.test(el?.className || '');
  for (const p of [...root.querySelectorAll('p')]) {
    if (!isItem(p) || !p.isConnected) continue;
    const items = [p];
    let x = p.nextElementSibling;
    while (isItem(x)) { items.push(x); x = x.nextElementSibling; }
    const marker = (items[0].textContent || '').trim();
    const list = doc.createElement(/^[\s\d]+[.)]/.test(marker) || /^[a-z][.)]\s/i.test(marker) ? 'ol' : 'ul');
    p.before(list);
    for (const it of items) {
      const li = doc.createElement('li');
      li.innerHTML = it.innerHTML;
      // The bullet / number Word typed in front.
      const first = li.firstChild;
      if (first?.nodeType === 3) first.data = first.data.replace(/^\s*([·•▪o§\-–]|\d+[.)]|[a-z][.)])\s+/i, '');
      list.appendChild(li);
      it.remove();
    }
  }
}

export function cleanPaste(html, options = {}) {
  opts = { tables: !!options.tables };
  // Excel puts bold / alignment in <style> classes (.xl65 { font-weight:700 }).
  xlClasses = {};
  for (const m of String(html || '').matchAll(/\.(xl\d+)\s*\{([^}]*)\}/g)) {
    const body = m[2].replace(/\s/g, '').toLowerCase();
    xlClasses[m[1]] = { b: /font-weight:(700|bold)/.test(body), align: (body.match(/text-align:(center|right|left)/) || [])[1] };
  }
  const src = String(html || '').replace(/<!--\[if[\s\S]*?<!\[endif\]-->/gi, '').replace(/<!--[\s\S]*?-->/g, '').replace(/<!--|-->/g, '');
  const doc = new DOMParser().parseFromString(src, 'text/html');
  wordLists(doc.body, doc);
  const out = doc.createElement('div');
  out.appendChild(walk(doc.body, doc));
  // Text left directly in the box (no paragraph) gets one.
  const loose = [...out.childNodes].filter((n) => n.nodeType === 3 ? n.data.trim() : !/^(P|H2|H3|BLOCKQUOTE|UL|OL|TABLE)$/.test(n.tagName));
  if (loose.length && loose.length === out.childNodes.length) {
    const p = doc.createElement('p');
    while (out.firstChild) p.appendChild(out.firstChild);
    out.appendChild(p);
  }
  // Empty paragraphs (Word spacing), nested paragraphs, stray line breaks.
  for (const p of [...out.querySelectorAll('p, h2, h3, li, blockquote')]) if (!p.textContent.trim() && !p.querySelector('br') && !p.closest('td, th')) p.remove();
  // A table needs a paragraph after it (to keep typing).
  for (const tb of [...out.querySelectorAll('table')]) if (!tb.nextElementSibling) tb.after(Object.assign(doc.createElement('p'), { innerHTML: '<br>' }));
  for (const p of [...out.querySelectorAll('p p, li p, h2 p, h3 p')]) { while (p.firstChild) p.before(p.firstChild); p.remove(); }
  return out.innerHTML.replace(/(<br>\s*){3,}/g, '<br><br>').trim();
}
